// Part of purchasePages, split by screen to keep files short; purchasePages.ts merges the parts.
export const en = {
  kinds: {
    bill: {
      label: 'Bill',
      lower: 'bill',
      numberLabel: 'Bill no.',
      dateLabel: 'Bill date',
      deleteConfirm: 'Delete this bill? This cannot be undone.',
      voidNotice: 'This bill is void. Its posting was reversed; the number stays in the sequence.',
      voidNoticeReason:
        'This bill is void: {{reason}}. Its posting was reversed; the number stays in the sequence.',
      editPosted: 'Saving changes reverses the original posting and posts the edited bill.',
      clientReview:
        "Your accountant reviews this bill and approves it before it is posted. Upload the supplier's bill under Documents if you have it.",
    },
    expense: {
      label: 'Expense',
      lower: 'expense',
      numberLabel: 'Ref no.',
      dateLabel: 'Payment date',
      deleteConfirm: 'Delete this expense? This cannot be undone.',
      voidNotice:
        'This expense is void. Its posting was reversed; the number stays in the sequence.',
      voidNoticeReason:
        'This expense is void: {{reason}}. Its posting was reversed; the number stays in the sequence.',
      editPosted: 'Saving changes reverses the original posting and posts the edited expense.',
      clientReview:
        "Your accountant reviews this expense and approves it before it is posted. Upload the supplier's bill under Documents if you have it.",
    },
    debitNote: {
      label: 'Debit note',
      lower: 'debit note',
      numberLabel: 'Debit note no.',
      dateLabel: 'Debit note date',
      deleteConfirm: 'Delete this debit note? This cannot be undone.',
      voidNotice:
        'This debit note is void. Its posting was reversed; the number stays in the sequence.',
      voidNoticeReason:
        'This debit note is void: {{reason}}. Its posting was reversed; the number stays in the sequence.',
      editPosted: 'Saving changes reverses the original posting and posts the edited debit note.',
      clientReview:
        "Your accountant reviews this debit note and approves it before it is posted. Upload the supplier's bill under Documents if you have it.",
    },
    purchaseOrder: {
      label: 'Purchase order',
      lower: 'purchase order',
      numberLabel: 'PO no.',
      dateLabel: 'Purchase order date',
      deleteConfirm: 'Delete this purchase order? This cannot be undone.',
      voidNotice:
        'This purchase order is void. Its posting was reversed; the number stays in the sequence.',
      voidNoticeReason:
        'This purchase order is void: {{reason}}. Its posting was reversed; the number stays in the sequence.',
      editPosted:
        'Saving changes reverses the original posting and posts the edited purchase order.',
      clientReview:
        "Your accountant reviews this purchase order and approves it before it is posted. Upload the supplier's bill under Documents if you have it.",
    },
    billPayment: { label: 'Bill payment' },
  },
  taxWord: { vat: 'VAT', gst: 'GST', vatShort: 'VAT', gstShort: 'GST' },
  checks: {
    overBalance:
      '{{number}}: payment and credit exceed the open balance of {{currency}} {{balance}}.',
    datedAfterPayment: '{{number}} is dated after the payment date.',
    vendorCredit: '{{name}} has {{currency}} {{available}} of credit; {{applied}} was applied.',
    enterAmount: 'Enter a payment or credit for at least one bill.',
    overBillBalance: '{{number}}: more than its open balance of {{balance}}.',
    overCredit: 'Applying {{total}} is more than the {{available}} of credit left.',
  },
  moneyBar: {
    unpaid: 'Unpaid',
    paid: 'Paid',
    overdue: 'overdue',
    notDue: 'open, not due yet',
    paidLast30: 'paid last 30 days',
    last365: 'Last 365 days',
    spend: 'bills and expenses, net of debit notes',
    toReview: 'To review',
    drafts: 'drafts',
    vendorCredit: 'vendor credit',
  },
  page: {
    selectClientHint: 'Use the client switcher in the top bar.',
    notFound: 'Document not found',
    wrongKind: 'This link points at a different kind of document.',
  },
  applyDebitNote: {
    title: 'Apply debit note {{number}}',
    description:
      '{{amount}} of credit from {{vendor}}. Applying posts nothing new: the debit note already reduced accounts payable.',
    applied: 'Debit note {{number}}: {{amount}} applied.',
    dateApplied: 'Date applied',
    openBills: 'Open bills',
    bill: 'Bill',
    dueDate: 'Due date',
    openBalance: 'Open balance',
    apply: 'Apply',
    applyTo: 'Apply to {{number}}',
    noOpenBills: 'This vendor has no open bills.',
    applying: 'Applying {{total}} of {{available}}.',
    applyingButton: 'Applying…',
  },
  form: {
    unsaved: 'You have unsaved changes. Leave without saving?',
    payee: 'Payee',
    vendor: 'Vendor',
    choosePayeeOptional: 'Choose a payee (optional)',
    chooseVendor: 'Choose a vendor',
    chooseVendorProblem: 'Choose a vendor.',
    addLine: 'Add at least one line.',
    choosePaymentAccount: 'Choose the payment account.',
    lineTaxCode: 'Line {{line}}: choose a {{tax}} code.',
    sentForApproval: '{{doc}} sent to your accountant for approval.',
    saved: '{{doc}} saved.',
    savedDraft: '{{doc}} saved as a draft.',
    reposted: '{{doc}} updated and reposted.',
    approvedPosted: '{{doc}} approved and posted.',
    view: 'View',
    voided: '{{doc}} voided.',
    deleted: '{{doc}} deleted.',
    poClosed: 'Purchase order {{number}} closed.',
    poReopened: 'Purchase order {{number}} reopened.',
    netDays: 'Net {{days}}',
    received: 'Received',
    purchaseOrder: 'Purchase order',
    class: 'Class',
    project: 'Project',
    classLine: 'Class, {{line}}',
    projectLine: 'Project, {{line}}',
    postedByDocuments: 'Posted to by documents only',
    taxLine: '{{tax}}, {{line}}',
    recoverableLine: 'Recoverable {{tax}} %, {{line}}',
    recoverableHint:
      'Share of the {{tax}} you can recover: 100 in full, 0 when blocked (staff meals, personal use), or your recovery rate',
    deleteLine: 'Delete {{line}}',
    amountLine: 'Amount, {{line}}',
    categoryLineN: 'category line {{n}}',
    itemLineN: 'item line {{n}}',
    revert: 'Revert',
    clear: 'Clear',
    payBill: 'Pay bill',
    applyToBills: 'Apply to bills',
    copyToBill: 'Copy to bill',
    closePurchaseOrder: 'Close purchase order',
    reopen: 'Reopen',
    receivedInFull: 'Received in full on bills',
    makeRecurring: 'Make recurring',
    more: 'More',
    copy: 'Copy',
    void: 'Void',
    delete: 'Delete',
    deleteDraft: 'Delete draft',
    transactionJournal: 'Transaction journal',
    voidEntry: 'Void entry',
    sending: 'Sending…',
    submitForApproval: 'Submit for approval',
    saveDraft: 'Save draft',
    approveAndClose: 'Approve and close',
    approveAndNew: 'Approve and new',
    moreSaveOptions: 'More save options',
    openingNotice:
      'Brought over from QuickBooks at the conversion date with its open balance. Its income or expense and VAT are in the opening balances, so it has no lines and cannot be edited; record payments against it as usual.',
    approved: 'Approved.',
    approvedBy: 'Approved by {{name}}.',
    approvedOn: 'Approved on {{date}}.',
    approvedByOn: 'Approved by {{name}} on {{date}}.',
    alreadyApplied: '{{amount}} is already paid or applied.',
    poNotice:
      'A purchase order is not posted: it records what you ordered. Bills receive it with Copy to bill; it closes when every line is received.',
    poClosedNote: 'Closed.',
    poReceivedLines: 'Lines received on bills cannot change; close the order instead.',
    staffOnly: 'Only your accounting team can change expenses and bills.',
    paymentAccount: 'Payment account *',
    chooseAccount: 'Choose an account',
    paymentMethod: 'Payment method',
    amount: 'Amount',
    credit: 'Credit',
    total: 'Total',
    balanceDue: 'Balance due',
    mailingAddress: 'Mailing address',
    noAddress: 'No address on the vendor.',
    trnLine: 'TRN {{trn}}',
    terms: 'Terms',
    chooseTerms: 'Choose terms',
    dueDate: 'Due date',
    billNo: 'Bill no.',
    refNo: 'Ref no.',
    vendorRefNo: 'Vendor ref no.',
    vendorInvoiceNumber: "The vendor's invoice number",
    expenseNo: 'Expense no.',
    poNo: 'PO no.',
    internalNo: 'Internal no.',
    assignedWhenSaved: 'Assigned when saved',
    assignedOnApproval: 'Assigned on approval',
    dateOfSupply: 'Date of supply',
    dateOfSupplyHint: 'If different from the bill date',
    originalBill: 'Original bill',
    reason: 'Reason',
    reasonPlaceholder: 'Returned goods, price adjustment…',
    amountsAre: 'Amounts are',
    exclusiveOf: 'Exclusive of {{tax}}',
    inclusiveOf: 'Inclusive of {{tax}}',
    outOfScopeOf: 'Out of scope of {{tax}}',
    categoryDetails: 'Category details',
    category: 'Category',
    description: 'Description',
    recoverable: 'Recoverable',
    categoryLine: 'Category, {{line}}',
    descriptionLine: 'Description, {{line}}',
    addLines: 'Add lines',
    clearAllLines: 'Clear all lines',
    itemDetails: 'Item details',
    productService: 'Product/service',
    qty: 'Qty',
    rate: 'Rate',
    productServiceLine: 'Product/service, {{line}}',
    qtyLine: 'Qty, {{line}}',
    rateLine: 'Rate, {{line}}',
    showItemDetails: 'Show item details',
    memo: 'Memo',
    appliedTo: 'Applied to',
    paymentsAndCredits: 'Payments and credits',
    exchangeLoss: 'Exchange loss {{amount}}',
    exchangeGain: 'Exchange gain {{amount}}',
    subtotal: 'Subtotal',
    vatLine: 'VAT {{rate}} ({{code}})',
    applied: 'Applied',
    creditRemaining: 'Credit remaining',
    selfAssessed: 'Reverse charge or import {{tax}} you account for (not paid to the vendor)',
    notRecoverable: '{{tax}} not recoverable (part of the cost)',
    vat: 'VAT',
    shortcutsApprove:
      'Shortcuts: Ctrl+Alt+S approve and close · Ctrl+Alt+D approve and new · Ctrl+Alt+X close. Amounts accept arithmetic such as 1200*1.05.',
    shortcutsSave:
      'Shortcuts: Ctrl+Alt+S save · Ctrl+Alt+D approve and new · Ctrl+Alt+X close. Amounts accept arithmetic such as 1200*1.05.',
    voidTitle: 'Void {{doc}} {{number}}',
    voidDescription:
      'Voiding posts a reversing entry, releases any payments or credits applied to it and keeps the number. This cannot be undone.',
    recoveryNone: 'None',
    recoveryFull: 'Full',
    recoverySelfAssessedHint:
      'Reverse charge and import {{tax}} is accounted for as output tax and recovered in full',
    recoveryBlockedHint:
      '{{code}}: the {{tax}} is part of the cost and is not reported on the return',
    openPurchaseOrders: 'Open purchase orders for this vendor:',
    addPurchaseOrder: 'Add {{number}} ({{amount}})',
  },
};

export const ar = {
  kinds: {
    bill: {
      label: 'فاتورة مورد',
      lower: 'فاتورة المورد',
      numberLabel: 'رقم فاتورة المورد',
      dateLabel: 'تاريخ فاتورة المورد',
      deleteConfirm: 'هل تريد حذف فاتورة المورد هذه؟ لا يمكن التراجع عن ذلك.',
      voidNotice: 'فاتورة المورد هذه ملغاة. عُكس ترحيلها، ويبقى رقمها في التسلسل.',
      voidNoticeReason:
        'فاتورة المورد هذه ملغاة: {{reason}}. عُكس ترحيلها، ويبقى رقمها في التسلسل.',
      editPosted: 'حفظ التغييرات يعكس الترحيل الأصلي ويرحّل فاتورة المورد بعد تعديلها.',
      clientReview:
        'يراجع محاسبك فاتورة المورد هذه ويعتمدها قبل ترحيلها. ارفع فاتورة المورد الأصلية ضمن المستندات إن كانت لديك.',
    },
    expense: {
      label: 'مصروف',
      lower: 'المصروف',
      numberLabel: 'الرقم المرجعي',
      dateLabel: 'تاريخ الدفع',
      deleteConfirm: 'هل تريد حذف هذا المصروف؟ لا يمكن التراجع عن ذلك.',
      voidNotice: 'هذا المصروف ملغى. عُكس ترحيله، ويبقى رقمه في التسلسل.',
      voidNoticeReason: 'هذا المصروف ملغى: {{reason}}. عُكس ترحيله، ويبقى رقمه في التسلسل.',
      editPosted: 'حفظ التغييرات يعكس الترحيل الأصلي ويرحّل المصروف بعد تعديله.',
      clientReview:
        'يراجع محاسبك هذا المصروف ويعتمده قبل ترحيله. ارفع فاتورة المورد ضمن المستندات إن كانت لديك.',
    },
    debitNote: {
      label: 'إشعار مدين',
      lower: 'الإشعار المدين',
      numberLabel: 'رقم الإشعار المدين',
      dateLabel: 'تاريخ الإشعار المدين',
      deleteConfirm: 'هل تريد حذف هذا الإشعار المدين؟ لا يمكن التراجع عن ذلك.',
      voidNotice: 'هذا الإشعار المدين ملغى. عُكس ترحيله، ويبقى رقمه في التسلسل.',
      voidNoticeReason: 'هذا الإشعار المدين ملغى: {{reason}}. عُكس ترحيله، ويبقى رقمه في التسلسل.',
      editPosted: 'حفظ التغييرات يعكس الترحيل الأصلي ويرحّل الإشعار المدين بعد تعديله.',
      clientReview:
        'يراجع محاسبك هذا الإشعار المدين ويعتمده قبل ترحيله. ارفع فاتورة المورد ضمن المستندات إن كانت لديك.',
    },
    purchaseOrder: {
      label: 'أمر شراء',
      lower: 'أمر الشراء',
      numberLabel: 'رقم أمر الشراء',
      dateLabel: 'تاريخ أمر الشراء',
      deleteConfirm: 'هل تريد حذف أمر الشراء هذا؟ لا يمكن التراجع عن ذلك.',
      voidNotice: 'أمر الشراء هذا ملغى. عُكس ترحيله، ويبقى رقمه في التسلسل.',
      voidNoticeReason: 'أمر الشراء هذا ملغى: {{reason}}. عُكس ترحيله، ويبقى رقمه في التسلسل.',
      editPosted: 'حفظ التغييرات يعكس الترحيل الأصلي ويرحّل أمر الشراء بعد تعديله.',
      clientReview:
        'يراجع محاسبك أمر الشراء هذا ويعتمده قبل ترحيله. ارفع فاتورة المورد ضمن المستندات إن كانت لديك.',
    },
    billPayment: { label: 'سداد فاتورة مورد' },
  },
  taxWord: {
    vat: 'ضريبة القيمة المضافة',
    gst: 'ضريبة السلع والخدمات',
    vatShort: 'الضريبة',
    gstShort: 'الضريبة',
  },
  checks: {
    overBalance:
      '{{number}}: الدفعة والرصيد الدائن يتجاوزان الرصيد المفتوح البالغ {{currency}} {{balance}}.',
    datedAfterPayment: 'تاريخ {{number}} لاحق لتاريخ الدفع.',
    vendorCredit: 'رصيد {{name}} الدائن {{currency}} {{available}}، وقد طُبّق {{applied}}.',
    enterAmount: 'أدخل دفعة أو رصيداً دائناً لفاتورة مورد واحدة على الأقل.',
    overBillBalance: '{{number}}: أكثر من رصيدها المفتوح البالغ {{balance}}.',
    overCredit: 'تطبيق {{total}} يتجاوز الرصيد الدائن المتبقي البالغ {{available}}.',
  },
  moneyBar: {
    unpaid: 'غير مدفوعة',
    paid: 'مدفوعة',
    overdue: 'متأخرة السداد',
    notDue: 'مفتوحة، لم يحن موعد استحقاقها',
    paidLast30: 'مدفوعة خلال آخر 30 يوماً',
    last365: 'آخر 365 يوماً',
    spend: 'فواتير الموردين والمصروفات، بعد خصم الإشعارات المدينة',
    toReview: 'للمراجعة',
    drafts: 'مسودات',
    vendorCredit: 'أرصدة دائنة لدى الموردين',
  },
  page: {
    selectClientHint: 'استخدم أداة تبديل العميل في الشريط العلوي.',
    notFound: 'المستند غير موجود',
    wrongKind: 'يشير هذا الرابط إلى نوع آخر من المستندات.',
  },
  applyDebitNote: {
    title: 'تطبيق الإشعار المدين {{number}}',
    description:
      'رصيد دائن بقيمة {{amount}} من {{vendor}}. التطبيق لا يرحّل شيئاً جديداً: فقد خفّض الإشعار المدين الذمم الدائنة مسبقاً.',
    applied: 'الإشعار المدين {{number}}: طُبّق {{amount}}.',
    dateApplied: 'تاريخ التطبيق',
    openBills: 'فواتير الموردين المفتوحة',
    bill: 'فاتورة المورد',
    dueDate: 'تاريخ الاستحقاق',
    openBalance: 'الرصيد المفتوح',
    apply: 'تطبيق',
    applyTo: 'التطبيق على {{number}}',
    noOpenBills: 'لا توجد فواتير مفتوحة لهذا المورد.',
    applying: 'تطبيق {{total}} من {{available}}.',
    applyingButton: 'جارٍ التطبيق…',
  },
  form: {
    unsaved: 'لديك تغييرات غير محفوظة. هل تريد المغادرة دون حفظ؟',
    payee: 'المستفيد',
    vendor: 'المورد',
    choosePayeeOptional: 'اختر المستفيد (اختياري)',
    chooseVendor: 'اختر المورد',
    chooseVendorProblem: 'اختر المورد.',
    addLine: 'أضف سطراً واحداً على الأقل.',
    choosePaymentAccount: 'اختر حساب الدفع.',
    lineTaxCode: 'السطر {{line}}: اختر رمز {{tax}}.',
    sentForApproval: 'أُرسل {{doc}} إلى محاسبك للاعتماد.',
    saved: 'تم حفظ {{doc}}.',
    savedDraft: 'تم حفظ {{doc}} كمسودة.',
    reposted: 'تم تحديث {{doc}} وإعادة ترحيله.',
    approvedPosted: 'تم اعتماد {{doc}} وترحيله.',
    view: 'عرض',
    voided: 'تم إلغاء {{doc}}.',
    deleted: 'تم حذف {{doc}}.',
    poClosed: 'تم إقفال أمر الشراء {{number}}.',
    poReopened: 'تمت إعادة فتح أمر الشراء {{number}}.',
    netDays: 'صافي {{days}} يوماً',
    received: 'المستلم',
    purchaseOrder: 'أمر الشراء',
    class: 'الفئة',
    project: 'المشروع',
    classLine: 'الفئة، {{line}}',
    projectLine: 'المشروع، {{line}}',
    postedByDocuments: 'تُرحَّل إليه المستندات فقط',
    taxLine: '{{tax}}، {{line}}',
    recoverableLine: 'نسبة {{tax}} القابلة للاسترداد %، {{line}}',
    recoverableHint:
      'حصة {{tax}} التي يمكنك استردادها: 100 للاسترداد الكامل، و0 عند الحظر (وجبات الموظفين، الاستخدام الشخصي)، أو نسبة الاسترداد الخاصة بك',
    deleteLine: 'حذف {{line}}',
    amountLine: 'المبلغ، {{line}}',
    categoryLineN: 'سطر التصنيف {{n}}',
    itemLineN: 'سطر الصنف {{n}}',
    revert: 'التراجع عن التغييرات',
    clear: 'مسح',
    payBill: 'سداد الفاتورة',
    applyToBills: 'التطبيق على فواتير الموردين',
    copyToBill: 'نسخ إلى فاتورة مورد',
    closePurchaseOrder: 'إقفال أمر الشراء',
    reopen: 'إعادة فتح',
    receivedInFull: 'مستلم بالكامل في فواتير الموردين',
    makeRecurring: 'جعله متكرراً',
    more: 'المزيد',
    copy: 'نسخ',
    void: 'إلغاء',
    delete: 'حذف',
    deleteDraft: 'حذف المسودة',
    transactionJournal: 'قيد المعاملة',
    voidEntry: 'قيد الإلغاء',
    sending: 'جارٍ الإرسال…',
    submitForApproval: 'إرسال للاعتماد',
    saveDraft: 'حفظ كمسودة',
    approveAndClose: 'اعتماد وإغلاق',
    approveAndNew: 'اعتماد وجديد',
    moreSaveOptions: 'خيارات حفظ أخرى',
    openingNotice:
      'نُقل من QuickBooks في تاريخ التحويل برصيده المفتوح. إيراده أو مصروفه وضريبة القيمة المضافة عليه مدرجة في الأرصدة الافتتاحية، لذا لا يحتوي على أسطر ولا يمكن تعديله؛ سجّل الدفعات مقابله كالمعتاد.',
    approved: 'معتمد.',
    approvedBy: 'اعتمده {{name}}.',
    approvedOn: 'اعتُمد في {{date}}.',
    approvedByOn: 'اعتمده {{name}} في {{date}}.',
    alreadyApplied: 'دُفع أو طُبّق منه {{amount}} مسبقاً.',
    poNotice:
      'أمر الشراء لا يُرحَّل: إنه يسجّل ما طلبته. تستلمه فواتير الموردين عبر «نسخ إلى فاتورة مورد»، ويُقفل عند استلام جميع أسطره.',
    poClosedNote: 'مُقفل.',
    poReceivedLines: 'لا يمكن تغيير الأسطر المستلمة في فواتير الموردين؛ أقفل الأمر بدلاً من ذلك.',
    staffOnly: 'لا يمكن تغيير المصروفات وفواتير الموردين إلا لفريق المحاسبة لديك.',
    paymentAccount: 'حساب الدفع *',
    chooseAccount: 'اختر حساباً',
    paymentMethod: 'طريقة الدفع',
    amount: 'المبلغ',
    credit: 'الرصيد الدائن',
    total: 'الإجمالي',
    balanceDue: 'الرصيد المستحق',
    mailingAddress: 'العنوان البريدي',
    noAddress: 'لا يوجد عنوان للمورد.',
    trnLine: 'رقم التسجيل الضريبي {{trn}}',
    terms: 'شروط الدفع',
    chooseTerms: 'اختر شروط الدفع',
    dueDate: 'تاريخ الاستحقاق',
    billNo: 'رقم فاتورة المورد',
    refNo: 'الرقم المرجعي',
    vendorRefNo: 'الرقم المرجعي للمورد',
    vendorInvoiceNumber: 'رقم الفاتورة لدى المورد',
    expenseNo: 'رقم المصروف',
    poNo: 'رقم أمر الشراء',
    internalNo: 'الرقم الداخلي',
    assignedWhenSaved: 'يُحدَّد عند الحفظ',
    assignedOnApproval: 'يُحدَّد عند الاعتماد',
    dateOfSupply: 'تاريخ التوريد',
    dateOfSupplyHint: 'إذا اختلف عن تاريخ فاتورة المورد',
    originalBill: 'فاتورة المورد الأصلية',
    reason: 'السبب',
    reasonPlaceholder: 'بضائع مرتجعة، تعديل سعر…',
    amountsAre: 'المبالغ',
    exclusiveOf: 'غير شاملة {{tax}}',
    inclusiveOf: 'شاملة {{tax}}',
    outOfScopeOf: 'خارج نطاق {{tax}}',
    categoryDetails: 'تفاصيل التصنيفات',
    category: 'التصنيف',
    description: 'الوصف',
    recoverable: 'القابل للاسترداد',
    categoryLine: 'التصنيف، {{line}}',
    descriptionLine: 'الوصف، {{line}}',
    addLines: 'إضافة أسطر',
    clearAllLines: 'مسح كل الأسطر',
    itemDetails: 'تفاصيل الأصناف',
    productService: 'المنتج/الخدمة',
    qty: 'الكمية',
    rate: 'السعر',
    productServiceLine: 'المنتج/الخدمة، {{line}}',
    qtyLine: 'الكمية، {{line}}',
    rateLine: 'السعر، {{line}}',
    showItemDetails: 'إظهار تفاصيل الأصناف',
    memo: 'ملاحظة',
    appliedTo: 'طُبّق على',
    paymentsAndCredits: 'الدفعات والأرصدة الدائنة',
    exchangeLoss: 'خسارة صرف {{amount}}',
    exchangeGain: 'ربح صرف {{amount}}',
    subtotal: 'المجموع الفرعي',
    vatLine: 'ضريبة القيمة المضافة {{rate}} ({{code}})',
    applied: 'المطبّق',
    creditRemaining: 'الرصيد الدائن المتبقي',
    selfAssessed: '{{tax}} على الاحتساب العكسي أو الاستيراد تحتسبها بنفسك (لا تُدفع للمورد)',
    notRecoverable: '{{tax}} غير القابلة للاسترداد (جزء من التكلفة)',
    vat: 'ضريبة القيمة المضافة',
    shortcutsApprove:
      'الاختصارات: Ctrl+Alt+S اعتماد وإغلاق · Ctrl+Alt+D اعتماد وجديد · Ctrl+Alt+X إغلاق. تقبل المبالغ العمليات الحسابية مثل 1200*1.05.',
    shortcutsSave:
      'الاختصارات: Ctrl+Alt+S حفظ · Ctrl+Alt+D اعتماد وجديد · Ctrl+Alt+X إغلاق. تقبل المبالغ العمليات الحسابية مثل 1200*1.05.',
    voidTitle: 'إلغاء {{doc}} {{number}}',
    voidDescription:
      'يرحّل الإلغاء قيداً عكسياً، ويحرّر أي دفعات أو أرصدة دائنة مطبّقة عليه، ويحتفظ بالرقم. لا يمكن التراجع عن ذلك.',
    recoveryNone: 'لا شيء',
    recoveryFull: 'كامل',
    recoverySelfAssessedHint:
      '{{tax}} على الاحتساب العكسي والاستيراد تُحتسب كضريبة مخرجات وتُسترد بالكامل',
    recoveryBlockedHint: '{{code}}: {{tax}} جزء من التكلفة ولا تُدرج في الإقرار',
    openPurchaseOrders: 'أوامر الشراء المفتوحة لهذا المورد:',
    addPurchaseOrder: 'إضافة {{number}} ({{amount}})',
  },
};
